import { useMsal } from "@azure/msal-react";
import { ChevronRight } from "@untitledui/icons";
import React, { Suspense, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useDeviceType } from "@/hooks/useDeviceType";
import DashboardSkeleton from "@/shared/components/DashboardSkeleton";
import WidgetLayout from "@/shared/components/WidgetLayout";
import type { WidgetConfig } from "@/types/index";
import { widgetComponentMap } from "@/types/index";
import { featureToWidgetMap } from "@/types/index";
import { widgetKeyToRoute } from "@/types/index";

import { selectFeatures, selectSelectedRole, selectAccessibilityOpen } from "../store/userSlice";
import FeedbackButton from "@/features/Feedback/Components/FeedbackButton";

const Dashboard: React.FC = () => {
  const { account } = useAuth();
  const { accounts } = useMsal();
  const { isMobile, isTablet, isDesktop } = useDeviceType(); //  added isMobile
  const { t } = useTranslation();

  useEffect(() => {
    document.title = "Daraja Platform";
  }, []);

  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");
  const userUpn = account?.username || "";

  const dashboardFeatures = useSelector(selectFeatures);
  const selectedRole = useSelector(selectSelectedRole);
  const accessibilityOpen = useSelector(selectAccessibilityOpen);
  const navigate = useNavigate();

  const tabletHiddenWidgets = ["dashboard.quiklinks"];

  const QuickActionsWidgets: Record<string, string> = {
  "dashboard.quiklinks": t("components.widgets.quickAccessApps.title"),
  "dashboard.myactiveitems": t("components.activePrograms.title"),
  "dashboard.taskpriorities": t("components.taskPriorities.title"),
  "dashboard.notifications": t("components.notificationTitle"),
  "dashboard.keyguidelines": t("components.keyGuidelines.title"),
  "dashboard.upcomingmeetings": t("components.MeetingsWidget.title"),
  "dashboard.impacttargets": t("components.widgets.financeTargets.title"),
  "dashboard.teamcalender": t("widgetSidebarConfig.TeamCalendar"),
  "dashboard.partnerdirectory": t("components.partnerDirectory.title"),
  "dashboard.learninginsights": t("widgetSidebarConfig.LearningInsights"),
  "dashboard.analyticsinsights": t("components.analyticsInsights.financialAnalytics"),
  "dashboard.programresources": t("components.widgets.programResources.title"),
  "dashboard.oversight": t("components.OversightWidget.title"),
  "dashboard.submissions": t("components.mySubmission.title"),
  "dashboard.partnersubmissions": t("components.partnerSubmission.title"),
  "dashboard.disbursementrequest": t("components.disbursementRequestsWrapper.title"),
  "dashboard.ideatracker": t("components.widgets.ideasTracker.title"),
  "dashboard.programimpact": t("components.widgets.programImpact.title"),
  "dashboard.pipelinepriority":t("components.PipelinePriority.title")
};

  /**
   *  MOBILE hidden widgets
   */
  const mobileHiddenWidgets = [
    "dashboard.quiklinks",
    "dashboard.partnerdirectory",
    "dashboard.disbursementrequest",
    "dashboard.partnersubmissions",
    "dashboard.programresources",
    "dashboard.dashboard",
    "dashboard.settings",
  ];

  /**
   * Widget Config
   */
  const dashboardWidgetConfig: Record<string, WidgetConfig> = useMemo(() => {
    return {
      "dashboard.quiklinks": { component: "QuickAccessApps", layout: "3" },
      "dashboard.myactiveitems": { component: "ActiveProgramsWidget", layout: "3" },
      "dashboard.taskpriorities": { component: "TaskPriorities", layout: "1" },
      "dashboard.notifications": {
        component: "NotificationsPanel",
        layout: selectedRole === "Partner" ? "3" : "2",
      },
      "dashboard.keyguidelines": { component: "KeyGuidelines", layout: "3" },
      "dashboard.upcomingmeetings": { component: "MeetingsWidget", layout: "3" },
      "dashboard.impacttargets": {
        component: "ImpactTargets",
        layout: "3",
      },
      "dashboard.teamcalender": { component: "TeamCalendarWidget", layout: "1" },
      "dashboard.partnerdirectory": { component: "PartnersDirectory", layout: "3" },
      "dashboard.learninginsights": { component: "LearningInsights", layout: "1" },
      "dashboard.analyticsinsights": { component: "AnalyticsWidget", layout: "3" },
      "dashboard.programresources": { component: "ProgramResources", layout: "3" },
      "dashboard.oversight": { component: "OversightWidget", layout: "3" },
      "dashboard.pipelinepriority": { component: "PipelinePriority", layout: "3" },
      "dashboard.pipeline-priority": { component: "PipelinePriority", layout: "3" },
      "dashboard.submissions": { component: "PartnerSubmissions", layout: "3" },
      "dashboard.partnersubmissions": { component: "PartnerSubmissions", layout: "3" },
      "dashboard.disbursementrequest": {
        component: "DisbursementRequestsWrapper",
        layout: "3",
      },
      "dashboard.ideatracker": { component: "IdeasTracker", layout: "3" },
      "dashboard.programimpact": { component: "ProgramImpact", layout: "3" },
    };
  }, [selectedRole]);

  /**
   * Access map
   */
  const featureAccessMap = useMemo(() => {
    const map: Record<string, boolean> = {};

    dashboardFeatures.forEach((feature: { feature_code: string; access: boolean | number }) => {
      map[feature.feature_code] = feature.access === true || feature.access === 1;
    });

    return map;
  }, [dashboardFeatures]);

  const handleRequestAccess = () => {
  window.open(
    "https://mastercardfdn.service-now.com/pulse",
    "_blank",
    "noopener,noreferrer"
  );
};

const { instance } = useMsal();

const handleSignOut = () => {
  instance.logoutRedirect();
};

const hasRoles = dashboardFeatures?.some(
  (feature) => featureAccessMap[feature.feature_code]
);

  /**
   *  MOBILE LIST (Quick Actions)
   */
  const getMobileWidgets = () => {
    return dashboardFeatures.filter(
      (feature) =>
        featureAccessMap[feature.feature_code] &&
        !mobileHiddenWidgets.includes(feature.feature_code)
    );
  };

  /**
   *  RENDER MOBILE VIEW
   */
  const renderMobileView = () => {
    const mobileWidgets = getMobileWidgets();

    return (
      <div className="px-4 py-4">
        <h2 className="text-lg font-semibold mb-4">{t("components.dashboard.QuickActions")}</h2>

        <div className="space-y-4">
          {mobileWidgets
            .filter((feature) => featureToWidgetMap[feature.feature_code])
            .map((feature) => (
              <div
                key={feature.feature_code}
                onClick={() => {
                  const widgetKey = featureToWidgetMap[feature.feature_code];
                  const route = widgetKeyToRoute[widgetKey];

                  if (route) {
                    navigate(`/app/${route}`);
                  }
                }}
                className="flex justify-between items-center p-4 rounded-xl border bg-white shadow-sm cursor-pointer"
              >
                <span className="font-medium">{QuickActionsWidgets[feature.feature_code]}</span>

                <ChevronRight className="w-5 h-5 text-gray-500" />
              </div>
            ))}
        </div>
      </div>
    );
  };

  /**
   *  DESKTOP WIDGET RENDER (UNCHANGED)
   */
  const renderWidgets = () =>
    dashboardFeatures.map((feature) => {
      if (!featureAccessMap[feature.feature_code]) return null;

      if (isTablet && tabletHiddenWidgets.includes(feature.feature_code)) {
        return null;
      }

      const config = dashboardWidgetConfig[feature.feature_code];
      if (!config) return null;

      const Component = widgetComponentMap[config.component];
      if (!Component) return null;

      return (
        <WidgetLayout
          key={feature.feature_code}
          layout={isDesktop ? config.layout : "3"}
          title={config.title}
        >
          <div id={feature.feature_code}>
            <Component userUpn={userUpn} />
          </div>
        </WidgetLayout>
      );
    });

  /**
   *  FINAL RETURN
   */
  return (
    <main className="bg-[var(--bg-main)] text-[var(--text-color)]">
      {/*  COMMON HEADER (DESKTOP + MOBILE with responsive sizing) */}
      <div className="px-4 pt-4 pb-2 sm:px-6 sm:pt-1">
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-4 sm:px-6 sm:py-4">
    <h2 className="text-xl font-bold sm:text-lg sm:font-semibold">
      {t("components.dashboard.welcomeMessage")} {displayName}!
    </h2>

    {!hasRoles && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-2xl">
      <h2 className="mb-4 text-2xl font-semibold text-gray-900">
        {t("components.dashboard.noAccessTitle")}
      </h2>

      <p className="mb-6 text-sm text-gray-600">
        {t("components.dashboard.noAccessMessage")}
      </p>

      <div className="flex justify-end gap-3">
        <button
          onClick={handleSignOut}
          className="rounded-lg border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-gray-50"
        >
          {t("components.dashboard.noAccessSignOutButton")}
        </button>

        <button
          onClick={handleRequestAccess}
          className="rounded-lg bg-[var(--color-brand)] px-5 py-2 font-medium text-white hover:opacity-90"
        >
          {t("components.dashboard.noAccessRequestButton")}
        </button>
      </div>
    </div>
  </div>
)}
  </div>
</div>

      {/*  CONTENT SWITCH */}
      <section className="dashboard-grid px-4 py-4">
        {isMobile ? (
          renderMobileView()
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Suspense fallback={<DashboardSkeleton />}>{renderWidgets()}</Suspense>
          </div>
        )}
      </section>

      {/*  FOOTER (HIDDEN ON MOBILE) */}
      {/* <div className="hidden sm:block w-full bg-neutral-100 px-8 py-4 text-sm text-neutral-700">
        <span className="font-semibold">{t("components.footer.links.accessibility")}:</span>
        <span>
          {" "}
          {t("components.footer.message")}{" "}
          <a
            href="https://mastercardfdn.org/en/contact/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-brand)] hover:underline"
          >
            https://mastercardfdn.org/en/contact/
          </a>
        </span>
      </div> */}
      {featureAccessMap["dashboard.feedback"] && !accessibilityOpen && <FeedbackButton />}
    </main>
  );
};

export default Dashboard;
